import { useEffect,useRef } from 'react'
import { useFrame,useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { fighters } from './content'
import { stageObjectDef } from './arenaStage'
import { ARENA_BOUNDS } from './simulation'
import { useFightStore } from './store'

type AlienTelemetry={emit:(type:string,payload:Record<string,unknown>)=>void}
type IntegrityDetail={
 heroId:string
 green?:boolean
 semantic?:string
 rootVelocity?:{x?:number;z?:number}
 deformationRepairs?:number
 faultStreak?:number
 pose?:{heightRatio?:number;diagonalRatio?:number;boneRatio?:number;current?:{width?:number;height?:number;depth?:number;diagonal?:number}}
 deformation?:{repairs?:number}
 meshIntegrity?:{quarantinedMeshes?:number}
 attachment?:{unknown?:number;managed?:number;derivedSplitRequired?:number}
 frameSync?:{blend?:number;phase?:number;substeps?:number}
}
type TimedCue={serial:number;contactMs:number;damageMs:number|null;reactionMs:number|null;skillStateMismatch:boolean;scene:string}

declare global{interface Window{__ALIEN_PLAYER_TELEMETRY__?:AlienTelemetry}}

const VFX_EVENT='inyron:fight-vfx'
const AUDIO_EVENT='inyron:fight-audio'
const finite=(v:unknown)=>typeof v==='number'&&Number.isFinite(v)
const emit=(type:string,payload:Record<string,unknown>)=>window.__ALIEN_PLAYER_TELEMETRY__?.emit(type,payload)

function segmentOccluded(
 camera:THREE.Camera,
 target:THREE.Vector3,
 stageObjects:ReturnType<typeof useFightStore.getState>['stageObjects'],
){
 const ax=camera.position.x,az=camera.position.z,bx=target.x,bz=target.z
 const dx=bx-ax,dz=bz-az,len2=dx*dx+dz*dz
 if(len2<.01)return 0
 for(const object of stageObjects){
  if(object.state==='collapsed')continue
  const def=stageObjectDef(object.id);if(!def)continue
  const ox=def.position[0],oz=def.position[2]
  const t=THREE.MathUtils.clamp(((ox-ax)*dx+(oz-az)*dz)/len2,0,1)
  if(t<=.06||t>=.94)continue
  const px=ax+dx*t,pz=az+dz*t,distance=Math.hypot(ox-px,oz-pz)
  const rayHeight=camera.position.y+(target.y-camera.position.y)*t
  if(distance<.48&&rayHeight<2.15)return 1
 }
 return 0
}

export function AlienPlayerTelemetryBridge(){
 const{camera}=useThree()
 const previousCamera=useRef(camera.quaternion.clone())
 const maxAngularJerk=useRef(0)
 const lastSampleAt=useRef(0)
 const integrity=useRef(new Map<string,IntegrityDetail>())
 const pending=useRef(new Map<number,TimedCue>())
 const vfxTimes=useRef(new Map<number,number>())
 const audioTimes=useRef(new Map<number,{at:number;buffered:boolean}>())
 const projected=useRef(new THREE.Vector3())

 useEffect(()=>{
  const onIntegrity=(event:Event)=>{
   const detail=(event as CustomEvent<IntegrityDetail>).detail
   if(!detail?.heroId)return
   integrity.current.set(detail.heroId,detail)
   const state=useFightStore.getState(),playerId=fighters[state.playerIndex]?.id,enemyId=fighters[state.enemyIndex]?.id
   const fighter=detail.heroId===playerId?state.player:detail.heroId===enemyId?state.enemy:null
   const pose=detail.pose
   const ratios=[pose?.heightRatio,pose?.diagonalRatio,pose?.boneRatio].filter(finite) as number[]
   const nanTransforms=[pose?.current?.width,pose?.current?.height,pose?.current?.depth,pose?.current?.diagonal,...ratios].filter(value=>value!==undefined&&!finite(value)).length
   const currentRepairs=Number(detail.deformationRepairs??0)
   const grounded=Boolean(fighter&&fighter.aerialMode==='ground'&&Math.abs(fighter.y)<.015)
   const footPlanted=Boolean(grounded&&detail.semantic==='idle')
   const rootSpeed=Math.hypot(Number(detail.rootVelocity?.x??0),Number(detail.rootVelocity?.z??0))
   const cue=state.impactCue
   emit('animation.sample',{
    scene:'fight',
    entity:detail.heroId,
    nanTransforms,
    boneScaleDeviation:currentRepairs>0?.5:0,
    footPlanted,
    footSlideSpeed:footPlanted?rootSpeed:0,
    contactErrorCm:cue?.contact?Math.max(0,(1-cue.contact.precision)*2.5):0,
    weaponAnchorErrorCm:Number(detail.attachment?.unknown??0)>0?4.5:0,
    meshPenetrationCm:currentRepairs>0?3:0,
    blendDiscontinuity:Number(detail.faultStreak??0)>0?1:0,
    frameSync:detail.frameSync??null,
    integrityGreen:detail.green!==false,
   })
  }
  const onVfx=(event:Event)=>{const detail=(event as CustomEvent<{serial:number;atMs:number}>).detail;if(detail?.serial!=null)vfxTimes.current.set(detail.serial,detail.atMs)}
  const onAudio=(event:Event)=>{const detail=(event as CustomEvent<{serial:number;atMs:number;buffered:boolean}>).detail;if(detail?.serial!=null)audioTimes.current.set(detail.serial,{at:detail.atMs,buffered:Boolean(detail.buffered)})}
  window.addEventListener('inyron:fighter-runtime-integrity',onIntegrity)
  window.addEventListener(VFX_EVENT,onVfx)
  window.addEventListener(AUDIO_EVENT,onAudio)
  const unsubscribe=useFightStore.subscribe((state,previous)=>{
   if(state.impactSerial===previous.impactSerial||!state.impactCue)return
   const cue=state.impactCue,now=performance.now(),attacker=cue.source==='player'?state.player:state.enemy,defender=cue.source==='player'?state.enemy:state.player,previousDefender=cue.source==='player'?previous.enemy:previous.player
   const damageMs=defender.health<previousDefender.health?now:null
   const reactionMs=defender.hitstun>previousDefender.hitstun||(!previousDefender.downed&&defender.downed)?now:null
   const expectedAttack=cue.attackKind,skillStateMismatch=Boolean(expectedAttack&&attacker.lastAttack&&expectedAttack!==attacker.lastAttack)
   pending.current.set(cue.serial,{serial:cue.serial,contactMs:now,damageMs,reactionMs,skillStateMismatch,scene:'fight'})
   window.setTimeout(()=>{
    const item=pending.current.get(cue.serial)
    if(!item)return
    const vfx=vfxTimes.current.get(cue.serial),audio=audioTimes.current.get(cue.serial)
    emit('combat.sample',{scene:item.scene,contactMs:item.contactMs,damageMs:item.damageMs,vfxMs:vfx??null,reactionMs:item.reactionMs,skillStateMismatch:item.skillStateMismatch})
    emit('audio.sample',{scene:item.scene,missingCue:audio?null:'impact-'+cue.serial,duplicateCue:false,desyncMs:audio?audio.at-item.contactMs:null,clipping:false,bufferedAsset:audio?.buffered??false})
    pending.current.delete(cue.serial);vfxTimes.current.delete(cue.serial);audioTimes.current.delete(cue.serial)
   },220)
  })
  return()=>{unsubscribe();window.removeEventListener('inyron:fighter-runtime-integrity',onIntegrity);window.removeEventListener(VFX_EVENT,onVfx);window.removeEventListener(AUDIO_EVENT,onAudio)}
 },[])

 useFrame(()=>{
  const state=useFightStore.getState()
  const runtimePerf=(window as typeof window&{__INYRON_RUNTIME_PERFORMANCE__?:{fps?:number;dpr?:number;pressure?:string;adjusted?:boolean}}).__INYRON_RUNTIME_PERFORMANCE__
  ;(window as typeof window&{__INYRON_FIGHT_ALIEN_STATE__?:Record<string,unknown>}).__INYRON_FIGHT_ALIEN_STATE__={
   status:state.status,paused:state.paused,impactSerial:state.impactSerial,impactKind:state.impactCue?.kind??null,impactAttack:state.impactCue?.attackKind??null,
   playerAttack:state.player.lastAttack,playerPhase:state.player.attackPhase,enemyAttack:state.enemy.lastAttack,enemyPhase:state.enemy.attackPhase,
   playerX:state.player.x,playerY:state.player.y,playerZ:state.player.z,enemyX:state.enemy.x,enemyY:state.enemy.y,enemyZ:state.enemy.z,
   stageTransitions:state.stageTransitions.length,hazards:state.arenaHazards.length,
   fps:runtimePerf?.fps??null,dpr:runtimePerf?.dpr??null,pressure:runtimePerf?.pressure??null,dprAdjusted:runtimePerf?.adjusted??false,
  }
  if(state.paused){
   previousCamera.current.copy(camera.quaternion)
   maxAngularJerk.current=0
   lastSampleAt.current=performance.now()
   return
  }
  const frameAngle=THREE.MathUtils.radToDeg(previousCamera.current.angleTo(camera.quaternion))
  maxAngularJerk.current=Math.max(maxAngularJerk.current,frameAngle)
  previousCamera.current.copy(camera.quaternion)
  const now=performance.now()
  if(now-lastSampleAt.current<500)return
  lastSampleAt.current=now
  const playerId=fighters[state.playerIndex]?.id??'',enemyId=fighters[state.enemyIndex]?.id??''
  const p=new THREE.Vector3(state.player.x,state.player.y+1.05,state.player.z),e=new THREE.Vector3(state.enemy.x,state.enemy.y+1.05,state.enemy.z)
  const outsideSafe=(point:THREE.Vector3)=>{
   projected.current.copy(point).project(camera)
   return Math.abs(projected.current.x)>.9||Math.abs(projected.current.y)>.92||projected.current.z<-1||projected.current.z>1
  }
  const targetOcclusionRatio=(segmentOccluded(camera,p,state.stageObjects)+segmentOccluded(camera,e,state.stageObjects))/2
  const near=Number((camera as THREE.PerspectiveCamera).near??.1),minTargetDistance=Math.min(camera.position.distanceTo(p),camera.position.distanceTo(e))
  const cameraInsideStage=state.stageObjects.some(object=>{const def=stageObjectDef(object.id);if(!def||object.state==='collapsed')return false;return Math.hypot(camera.position.x-def.position[0],camera.position.z-def.position[2])<.42&&camera.position.y<2.15})
  emit('camera.sample',{scene:'fight',insideGeometry:cameraInsideStage,targetOcclusionRatio,angularJerk:maxAngularJerk.current,targetOutsideSafeFrame:outsideSafe(p)||outsideSafe(e)?'fighter':null,nearClipIntersection:minTargetDistance<near+.35?{distance:minTargetDistance,near}:null,position:[camera.position.x,camera.position.y,camera.position.z]})
  maxAngularJerk.current=0

  const finiteState=[state.player.x,state.player.y,state.player.z,state.enemy.x,state.enemy.y,state.enemy.z].every(Number.isFinite)
  const scaleOutlier=state.player.x<ARENA_BOUNDS.minX-.1||state.player.x>ARENA_BOUNDS.maxX+.1||state.enemy.x<ARENA_BOUNDS.minX-.1||state.enemy.x>ARENA_BOUNDS.maxX+.1||state.player.z<ARENA_BOUNDS.minZ-.1||state.player.z>ARENA_BOUNDS.maxZ+.1||state.enemy.z<ARENA_BOUNDS.minZ-.1||state.enemy.z>ARENA_BOUNDS.maxZ+.1
  const terrainGap=(state.player.aerialMode==='ground'&&state.player.y<-.02)||(state.enemy.aerialMode==='ground'&&state.enemy.y<-.02)
  emit('world.sample',{scene:'fight',terrainGap,floatingObjects:0,unreachableObjective:null,navDeadEndUnexpected:false,scaleOutlier:scaleOutlier?'fighter-outside-arena-bounds':null,semanticInvalid:finiteState?null:'non-finite-combatant-transform',position:[(state.player.x+state.enemy.x)/2,0,(state.player.z+state.enemy.z)/2]})

  const collisionApplies=state.player.quantumTimer<=0&&state.enemy.quantumTimer<=0&&Math.abs(state.player.z-state.enemy.z)<=.72&&Math.abs(state.player.y-state.enemy.y)<=1.15
  const minGap=state.player.hurtHalfWidth+state.enemy.hurtHalfWidth+.03
  const penetrationCm=collisionApplies?Math.max(0,minGap-Math.abs(state.player.x-state.enemy.x))*100:0
  const floatingCm=Math.max(state.player.aerialMode==='ground'?Math.abs(state.player.y)*100:0,state.enemy.aerialMode==='ground'?Math.abs(state.enemy.y)*100:0)
  const playerIntegrity=integrity.current.get(playerId),enemyIntegrity=integrity.current.get(enemyId)
  const integrityFault=[playerIntegrity,enemyIntegrity].some(item=>Number(item?.deformationRepairs??0)>0||Number(item?.meshIntegrity?.quarantinedMeshes??0)>0)
  emit('physics.sample',{scene:'fight',tunneling:scaleOutlier,penetrationCm,floatingCm,accessoryDetachedCm:integrityFault?4.5:0,clothExplosion:integrityFault,body:scaleOutlier?'fighter-boundary':null,pair:'player-enemy'})
 })
 return null
}
